import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { resolveAvatarUrl } from "@/lib/avatarApi";
import { formatBytes } from "@/lib/cropAvatarImage";
import AvatarCropDialog from "@/components/avatars/AvatarCropDialog";
import BannerCropDialog from "@/components/avatars/BannerCropDialog";

/**
 * Shared editor: preview, local file (crop + compress), HTTPS URL, save, remove.
 * @param {boolean} [crop=true] Open a crop dialog after file pick.
 * @param {"circle"|"rect"} [previewShape="circle"] Preview chrome + which crop UI.
 */
export default function AvatarSourceEditor({
  label = "Avatar",
  currentUrl,
  previewFallback,
  onSaveUrl,
  onUploadFile,
  onClear,
  disabled = false,
  crop = true,
  previewShape = "circle",
}) {
  const fileRef = useRef(null);
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const [localPreview, setLocalPreview] = useState(null);
  const [cropSource, setCropSource] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const isBanner = previewShape === "rect";

  useEffect(() => {
    setUrl("");
    setFile(null);
    setLocalPreview(null);
    setCropSource(null);
    setCropOpen(false);
    setError(null);
  }, [currentUrl]);

  useEffect(() => {
    if (!file) {
      setLocalPreview(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(file);
    setLocalPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  const previewSrc = useMemo(() => {
    if (localPreview) return localPreview;
    return resolveAvatarUrl(currentUrl);
  }, [localPreview, currentUrl]);

  const handleFilePick = (e) => {
    const picked = e.target.files?.[0] || null;
    if (!picked) return;
    setError(null);
    setUrl("");
    if (crop) {
      setCropSource(picked);
      setCropOpen(true);
    } else {
      setFile(picked);
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleCropped = (croppedFile) => {
    setFile(croppedFile);
    setCropSource(null);
  };

  const handleCropOpenChange = (open) => {
    setCropOpen(open);
    if (!open) setCropSource(null);
  };

  const handleSave = async () => {
    if (disabled || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (file) {
        await onUploadFile?.(file);
      } else if (url.trim()) {
        await onSaveUrl?.(url.trim());
      } else {
        setError("Choose a file or enter an HTTPS URL");
        return;
      }
      setFile(null);
      setUrl("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to update image"
      );
    } finally {
      setBusy(false);
    }
  };

  const handleClear = async () => {
    if (disabled || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onClear?.();
      setFile(null);
      setUrl("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to remove image"
      );
    } finally {
      setBusy(false);
    }
  };

  const hint = isBanner
    ? "Upload JPEG/PNG/WebP — crop to match your cover — or paste an HTTPS URL"
    : "Upload JPEG/PNG/WebP (crop to circle) or paste an HTTPS image URL";

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex items-center gap-3">
        <div
          className={
            isBanner
              ? "h-12 w-36 shrink-0 overflow-hidden rounded-md bg-muted flex items-center justify-center"
              : "h-14 w-14 shrink-0 overflow-hidden rounded-full bg-muted flex items-center justify-center"
          }
        >
          {previewSrc ? (
            <img
              src={previewSrc}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            previewFallback || (
              <span className="text-xs text-muted-foreground">None</span>
            )
          )}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium">{label}</p>
          <p className="text-xs text-muted-foreground">{hint}</p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`avatar-file-${label}`}>Local file</Label>
        <Input
          id={`avatar-file-${label}`}
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          disabled={disabled || busy}
          onChange={handleFilePick}
        />
        {file ? (
          <p className="text-xs text-muted-foreground">
            Ready to upload · {formatBytes(file.size)}
            <button
              type="button"
              className="ml-2 text-primary underline-offset-2 hover:underline"
              disabled={disabled || busy}
              onClick={() => {
                setFile(null);
                if (fileRef.current) fileRef.current.value = "";
              }}
            >
              Clear selection
            </button>
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor={`avatar-url-${label}`}>HTTPS URL</Label>
        <Input
          id={`avatar-url-${label}`}
          type="url"
          placeholder="https://…"
          value={url}
          disabled={disabled || busy || !!file}
          onChange={(e) => setUrl(e.target.value)}
        />
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          disabled={disabled || busy || (!file && !url.trim())}
          onClick={handleSave}
        >
          {busy ? "Saving…" : "Save"}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled || busy || !currentUrl}
          onClick={handleClear}
        >
          Remove
        </Button>
      </div>

      {crop && isBanner ? (
        <BannerCropDialog
          open={cropOpen}
          file={cropSource}
          onOpenChange={handleCropOpenChange}
          onCropped={handleCropped}
        />
      ) : null}
      {crop && !isBanner ? (
        <AvatarCropDialog
          open={cropOpen}
          file={cropSource}
          onOpenChange={handleCropOpenChange}
          onCropped={handleCropped}
        />
      ) : null}
    </div>
  );
}
